/**
 * Centralized Excel Export Utility
 * Provides consistent Excel (.xlsx) download across all tables in the project.
 * Uses SheetJS (xlsx) for proper Excel file generation.
 */
import * as XLSX from 'xlsx'

export interface ExcelColumn {
    /** Column header label shown in Excel */
    header: string
    /** Key path to extract value from row data (supports dot notation) */
    key: string
    /** Optional formatter function to transform cell values */
    format?: (value: any, row: any) => string | number
    /** Column width in characters (default: 15) */
    width?: number
}

export interface ExcelExportConfig {
    /** Sheet name in the Excel file */
    sheetName: string
    /** File name (without .xlsx extension) */
    fileName: string
    /** Column definitions */
    columns: ExcelColumn[]
    /** Row data array */
    data: any[]
}

/**
 * Extract a nested value from an object using a dot-separated key path.
 */
const getNestedValue = (obj: any, path: string): any => {
    return path.split('.').reduce((current, key) => current?.[key], obj)
}

/**
 * Export data to an Excel (.xlsx) file and trigger download.
 */
export const exportToExcel = (config: ExcelExportConfig): void => {
    const { sheetName, fileName, columns, data } = config

    if (!data || data.length === 0) {
        console.warn('[ExcelExport] No data to export')
        return
    }

    // Build header row
    const headers = columns.map((col) => col.header)

    // Build data rows
    const rows = data.map((row) =>
        columns.map((col) => {
            const rawValue = getNestedValue(row, col.key)
            if (col.format) {
                return col.format(rawValue, row)
            }
            return rawValue ?? ''
        }),
    )

    // Create worksheet
    const wsData = [headers, ...rows]
    const ws = XLSX.utils.aoa_to_sheet(wsData)

    // Set column widths
    ws['!cols'] = columns.map((col) => ({
        wch: col.width ?? 15,
    }))

    // Create workbook
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, sheetName.substring(0, 31)) // Excel limits sheet name to 31 chars

    // Generate timestamp suffix for unique file names
    const now = new Date()
    const timestamp = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}_${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}`

    // Trigger download
    XLSX.writeFile(wb, `${fileName}_${timestamp}.xlsx`)
}

// ─── Pre-built Export Configurations for Each Table ─────────────────────────

/**
 * Format a timestamp value for Excel display.
 */
const formatTimestamp = (val: any, row: any): string => {
    const raw = String(row.timestampIST || row.timestamp || row.TimeStamp || row.created_at || '')
    if (!raw) return '-'
    try {
        // Try to get clean time + date
        const d = new Date(raw)
        if (!isNaN(d.getTime())) {
            return d.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })
        }
    } catch (e) { console.warn('Ignored error in excel-export.ts:', e); }
    return raw
}

/**
 * Export Variance History table data to Excel.
 */
export const exportVarianceHistory = (data: any[], symbol: string): void => {
    exportToExcel({
        sheetName: 'Variance History',
        fileName: `Variance_History_${symbol}`,
        columns: [
            { header: 'Date Time', key: 'timestamp', format: formatTimestamp, width: 22 },
            {
                header: 'Max Diff %',
                key: 'max_oi_diff',
                format: (_, row) => Number(row.max_oi_diff ?? row.maxDiff ?? row.variance?.maxDiff ?? 0).toFixed(2),
                width: 12,
            },
            {
                header: 'Max Strike',
                key: 'max_strike',
                format: (_, row) => row.max_strike ?? row.maxStrike ?? row.variance?.maxStrike ?? 0,
                width: 12,
            },
            {
                header: 'Min Diff %',
                key: 'min_oi_diff',
                format: (_, row) => Number(row.min_oi_diff ?? row.minDiff ?? row.variance?.minDiff ?? 0).toFixed(2),
                width: 12,
            },
            {
                header: 'Min Strike',
                key: 'min_strike',
                format: (_, row) => row.min_strike ?? row.minStrike ?? row.variance?.minStrike ?? 0,
                width: 12,
            },
            { header: 'CE Count', key: 'ceCount', format: (_, row) => row.ce_count ?? row.ceCount ?? 0, width: 10 },
            { header: 'PE Count', key: 'peCount', format: (_, row) => row.pe_count ?? row.peCount ?? 0, width: 10 },
            {
                header: 'Total Count',
                key: 'totalCount',
                format: (_, row) => row.total_count ?? row.totalCount ?? (row.ce_count ?? row.ceCount ?? 0) + (row.pe_count ?? row.peCount ?? 0),
                width: 12,
            },
            {
                header: 'Variance',
                key: 'variance',
                format: (_, row) =>
                    Number(
                        typeof row.variance === 'number' ? row.variance : (row.variance?.finalVariance ?? row.finalVariance ?? 0),
                    ).toFixed(2),
                width: 12,
            },
            { header: 'Shift', key: 'shift', format: (_, row) => Number(row.shift ?? 0).toFixed(0), width: 10 },
            {
                header: 'Shift Old',
                key: 'shiftOld',
                format: (_, row) => Number(row.shiftOld ?? row.shift_old ?? 0).toFixed(0),
                width: 10,
            },
            { header: 'ATM', key: 'atm', format: (_, row) => row.atm ?? row.atmStrike ?? 0, width: 10 },
        ],
        data,
    })
}

/**
 * Export Crossover History table data to Excel.
 */
export const exportCrossoverHistory = (data: any[], symbol: string): void => {
    exportToExcel({
        sheetName: 'Crossover History',
        fileName: `Crossover_History_${symbol}`,
        columns: [
            { header: 'Date Time', key: 'timestamp', format: formatTimestamp, width: 22 },
            { header: 'Rank', key: 'rank', format: (_, row) => row.rank || row.Rank || 0, width: 8 },
            { header: 'CE LTP', key: 'ceLtp', format: (_, row) => Number(row.ceLtp ?? row.CE_LTP ?? 0).toFixed(2), width: 10 },
            {
                header: 'Resistance OI/Vol',
                key: 'resistanceValue',
                format: (_, row) => row.resistanceValue ?? 0,
                width: 15,
            },
            {
                header: 'Resistance Strike',
                key: 'resistanceStrike',
                format: (_, row) => row.resistanceStrike ?? 0,
                width: 16,
            },
            {
                header: 'Resistance 2',
                key: 'resistanceStrike2',
                format: (_, row) => row.resistanceStrike2 ?? 0,
                width: 14,
            },
            {
                header: 'Support Strike',
                key: 'supportStrike',
                format: (_, row) => row.supportStrike ?? 0,
                width: 14,
            },
            {
                header: 'Support 2',
                key: 'supportStrike2',
                format: (_, row) => row.supportStrike2 ?? 0,
                width: 12,
            },
            {
                header: 'Support OI/Vol',
                key: 'supportValue',
                format: (_, row) => row.supportValue ?? 0,
                width: 15,
            },
            { header: 'PE LTP', key: 'peLtp', format: (_, row) => Number(row.peLtp ?? row.PE_LTP ?? 0).toFixed(2), width: 10 },
            { header: 'CE Count', key: 'ceCount', format: (_, row) => row.ceCount ?? row.ce_count ?? 0, width: 10 },
            { header: 'PE Count', key: 'peCount', format: (_, row) => row.peCount ?? row.pe_count ?? 0, width: 10 },
            {
                header: 'Final Count',
                key: 'totalCount',
                format: (_, row) => row.totalCount ?? row.total_count ?? 0,
                width: 12,
            },
            {
                header: 'Max Diff %',
                key: 'max_oi_diff',
                format: (_, row) => Number(row.max_oi_diff ?? row.maxDiff ?? row.variance?.maxDiff ?? 0).toFixed(2),
                width: 12,
            },
            {
                header: 'Min Diff %',
                key: 'min_oi_diff',
                format: (_, row) => Number(row.min_oi_diff ?? row.minDiff ?? row.variance?.minDiff ?? 0).toFixed(2),
                width: 12,
            },
            {
                header: 'Variance',
                key: 'variance',
                format: (_, row) =>
                    Number(
                        typeof row.variance === 'number' ? row.variance : (row.variance?.finalVariance ?? row.finalVariance ?? 0),
                    ).toFixed(2),
                width: 12,
            },
            { header: 'Shift', key: 'shift', format: (_, row) => Number(row.shift ?? 0).toFixed(0), width: 10 },
            {
                header: 'Shift Old',
                key: 'shiftOld',
                format: (_, row) => Number(row.shiftOld ?? row.shift_old ?? 0).toFixed(0),
                width: 10,
            },
            { header: 'ATM', key: 'atm', format: (_, row) => row.atm ?? row.atmStrike ?? 0, width: 10 },
        ],
        data,
    })
}

/**
 * Export Dashboard Intraday History table data to Excel.
 */
export const exportDashboardHistory = (data: any[], symbol: string): void => {
    exportToExcel({
        sheetName: 'Intraday History',
        fileName: `Intraday_History_${symbol}`,
        columns: [
            { header: 'Date Time', key: 'timestamp', format: formatTimestamp, width: 22 },
            { header: 'Resistance', key: 'resistance', format: (_, row) => row.resistance ?? 0, width: 12 },
            {
                header: 'R LTP',
                key: 'resistanceLtp',
                format: (_, row) => Number(row.resistanceLtp ?? 0).toFixed(2),
                width: 10,
            },
            { header: 'Resistance 2', key: 'resistance2', format: (_, row) => row.resistance2 ?? 0, width: 14 },
            {
                header: 'R2 LTP',
                key: 'resistance2Ltp',
                format: (_, row) => Number(row.resistance2Ltp ?? 0).toFixed(2),
                width: 10,
            },
            { header: 'Support', key: 'support', format: (_, row) => row.support ?? 0, width: 12 },
            {
                header: 'S LTP',
                key: 'supportLtp',
                format: (_, row) => Number(row.supportLtp ?? 0).toFixed(2),
                width: 10,
            },
            { header: 'Shift', key: 'shift', format: (_, row) => Number(row.shift ?? 0).toFixed(0), width: 10 },
        ],
        data,
    })
}

/**
 * Export Inspector History table data to Excel.
 * This table has a dynamic multi-strike structure, so we build columns dynamically.
 */
export const exportInspectorHistory = (
    data: any[],
    selectedStrikes: number[],
    symbol: string,
    expiry: string,
): void => {
    // Build dynamic columns based on selected strikes
    const columns: ExcelColumn[] = [{ header: 'Date Time', key: 'timestamp', format: formatTimestamp, width: 22 }]

    const metricPairs = [
        { label: 'LTP', ceField: 'callPrice', peField: 'putPrice' },
        { label: 'OI', ceField: 'callOpenInterest', peField: 'putOpenInterest' },
        { label: 'Volume', ceField: 'callVolume', peField: 'putVolume' },
    ]

    for (const strike of selectedStrikes) {
        for (const metric of metricPairs) {
            columns.push({
                header: `${strike} CE ${metric.label}`,
                key: `strike_${strike}_ce_${metric.label}`,
                width: 16,
                format: (_, row) => {
                    const optionsData = row.optionsData
                    if (!optionsData?.[symbol]) return ''
                    const symbolData = optionsData[symbol]
                    let options = symbolData?.[expiry || ''] || []
                    if (options.length === 0) {
                        const keys = Object.keys(symbolData || {})
                        if (keys.length > 0 && keys[0]) options = symbolData[keys[0]] || []
                    }
                    const opt = options.find((o: any) => o.strike === strike)
                    return opt ? Number(opt[metric.ceField] ?? 0) : ''
                },
            })
            columns.push({
                header: `${strike} PE ${metric.label}`,
                key: `strike_${strike}_pe_${metric.label}`,
                width: 16,
                format: (_, row) => {
                    const optionsData = row.optionsData
                    if (!optionsData?.[symbol]) return ''
                    const symbolData = optionsData[symbol]
                    let options = symbolData?.[expiry || ''] || []
                    if (options.length === 0) {
                        const keys = Object.keys(symbolData || {})
                        if (keys.length > 0 && keys[0]) options = symbolData[keys[0]] || []
                    }
                    const opt = options.find((o: any) => o.strike === strike)
                    return opt ? Number(opt[metric.peField] ?? 0) : ''
                },
            })
        }
    }

    exportToExcel({
        sheetName: 'Inspector History',
        fileName: `Inspector_History_${symbol}`,
        columns,
        data,
    })
}

/**
 * Export Market Watch (Analysis) Intraday History table data to Excel.
 * This table has processed top-5 strike data per snapshot.
 */
export const exportMarketWatchHistory = (
    processedHistory: any[],
    activeSubTab: string,
    symbol: string,
): void => {
    const valKeyMap: Record<string, string> = {
        'Combined OI': 'combOI',
        'Combined Volume': 'combVol',
        'Top OI': 'oi',
        'Top Volume': 'volume',
        'Top Chng OI': 'oiChg',
    }
    const valKey = valKeyMap[activeSubTab] || 'oi'

    const columns: ExcelColumn[] = [
        {
            header: 'Date Time',
            key: 'time',
            format: (_, row) => (row.isLive ? 'LIVE' : `${row.time || '-'} ${row.fullDate || ''}`),
            width: 22,
        },
    ]

    // Top 1–5 columns
    for (let i = 0; i < 5; i++) {
        columns.push({
            header: `Top ${i + 1} Type`,
            key: `top5.${i}.type`,
            format: (_, row) => row.top5?.[i]?.type ?? '',
            width: 10,
        })
        columns.push({
            header: `Top ${i + 1} Strike`,
            key: `top5.${i}.strike`,
            format: (_, row) => row.top5?.[i]?.strike ?? '',
            width: 12,
        })
        columns.push({
            header: `Top ${i + 1} ${activeSubTab}`,
            key: `top5.${i}.value`,
            format: (_, row) => row.top5?.[i]?.[valKey] ?? '',
            width: 16,
        })
    }

    columns.push({
        header: 'Index LTP',
        key: 'spotPrice',
        format: (_, row) => Number(row.spotPrice ?? 0).toFixed(2),
        width: 14,
    })

    exportToExcel({
        sheetName: 'Market Watch History',
        fileName: `Market_Watch_History_${symbol}_${activeSubTab.replace(/\s/g, '_')}`,
        columns,
        data: processedHistory,
    })
}

function getMinuteVolDiff(opts: any[], isOtm: boolean) {
    if (!opts || opts.length === 0) return null
    let ceVol = 0
    let peVol = 0
    for (const opt of opts) {
        if (!opt) continue
        if (opt.type === 'CE') ceVol += (Number(opt.volume) || 0)
        else if (opt.type === 'PE') peVol += (Number(opt.volume) || 0)
    }
    if (ceVol === 0 && peVol === 0) return null
    return isOtm ? peVol - ceVol : ceVol - peVol
}

/**
 * Export Market Watch Split History table data to Excel.
 */
export const exportMarketWatchHistorySplit = (
    enrichedHistory: any[],
    activeSubTab: string,
    symbol: string,
): void => {
    const valKeyMap: Record<string, string> = {
        'Combined OI': 'combOI',
        'Combined Volume': 'combVol',
        'Top OI': 'oi',
        'Top Volume': 'volume',
        'Top Chng OI': 'oiChg',
    }
    const valKey = valKeyMap[activeSubTab] || 'oi'

    const columns: ExcelColumn[] = [
        {
            header: 'Date Time',
            key: 'time',
            format: (_, row) => (row.isLive ? 'LIVE' : `${row.time || '-'} ${row.fullDate || ''}`),
            width: 22,
        },
    ]

    // ITM Top 1–5 columns
    for (let i = 0; i < 5; i++) {
        columns.push({ header: `ITM Top ${i + 1} Type`, key: `itmTop5.${i}.type`, format: (_, row) => row.itmTop5?.[i]?.type ?? '', width: 12 })
        columns.push({ header: `ITM Top ${i + 1} Strike`, key: `itmTop5.${i}.strike`, format: (_, row) => row.itmTop5?.[i]?.strike ?? '', width: 14 })
        columns.push({ header: `ITM Top ${i + 1} Value`, key: `itmTop5.${i}.value`, format: (_, row) => row.itmTop5?.[i]?.[valKey] ?? '', width: 16 })
    }
    columns.push({ header: 'ITM Ind %', key: 'itmPct', format: (_, row) => row.itmPct ?? 0, width: 12 })
    columns.push({ header: 'ITM Vol Diff', key: 'itmVolDiff', format: (_, row) => getMinuteVolDiff(row.itmTop5, false) ?? 0, width: 14 })

    // OTM Top 1–5 columns
    for (let i = 0; i < 5; i++) {
        columns.push({ header: `OTM Top ${i + 1} Type`, key: `otmTop5.${i}.type`, format: (_, row) => row.otmTop5?.[i]?.type ?? '', width: 12 })
        columns.push({ header: `OTM Top ${i + 1} Strike`, key: `otmTop5.${i}.strike`, format: (_, row) => row.otmTop5?.[i]?.strike ?? '', width: 14 })
        columns.push({ header: `OTM Top ${i + 1} Value`, key: `otmTop5.${i}.value`, format: (_, row) => row.otmTop5?.[i]?.[valKey] ?? '', width: 16 })
    }
    columns.push({ header: 'OTM Ind %', key: 'otmPct', format: (_, row) => row.otmPct ?? 0, width: 12 })
    columns.push({ header: 'OTM Comb %', key: 'combPct', format: (_, row) => row.combPct ?? 0, width: 12 })
    columns.push({ header: 'OTM Vol Diff', key: 'otmVolDiff', format: (_, row) => getMinuteVolDiff(row.otmTop5, true) ?? 0, width: 14 })

    columns.push({
        header: 'Index LTP',
        key: 'spotPrice',
        format: (_, row) => Number(row.spotPrice ?? 0).toFixed(2),
        width: 14,
    })

    exportToExcel({
        sheetName: 'Market Watch Split History',
        fileName: `Market_Watch_Split_History_${symbol}_${activeSubTab.replace(/\s/g, '_')}`,
        columns,
        data: enrichedHistory,
    })
}

/**
 * Export ITM/OTM Analysis table data to Excel.
 */
export const exportItmOtmHistory = (data: any[], symbol: string, switcher: string): void => {
    exportToExcel({
        sheetName: 'ITM OTM Analysis',
        fileName: `ITM_OTM_${symbol}_${switcher}`,
        columns: [
            { header: 'Date Time', key: 'timestamp', format: formatTimestamp, width: 22 },
            { header: 'ATM Strike', key: 'atmStrike', format: (_, row) => row.atmStrike ?? 0, width: 12 },
            { header: 'ITM Call', key: 'itmCall', format: (_, row) => row.itmCall ?? 0, width: 14 },
            { header: 'ITM Put', key: 'itmPut', format: (_, row) => row.itmPut ?? 0, width: 14 },
            { header: 'ITM Net', key: 'itmNet', format: (_, row) => row.itmNet ?? 0, width: 14 },
            { header: 'OTM Call', key: 'otmCall', format: (_, row) => row.otmCall ?? 0, width: 14 },
            { header: 'OTM Put', key: 'otmPut', format: (_, row) => row.otmPut ?? 0, width: 14 },
            { header: 'OTM Net', key: 'otmNet', format: (_, row) => row.otmNet ?? 0, width: 14 },
            { header: 'Index RSI', key: 'rsiVal', format: (_, row) => (row.rsiVal > 0 ? row.rsiVal.toFixed(2) : '-'), width: 10 },
            { header: 'Index LTP', key: 'indexLtp', format: (_, row) => Number(row.indexLtp ?? 0).toFixed(2), width: 14 },
        ],
        data,
    })
}
